import { getSocket } from '@/services/socket/websocket.service.js'
import firebase from 'firebase/compat/app'
import { useAuthStore } from '@/stores/auth.store.js'
import 'firebase/compat/auth'
import {
    updateOrderInMessage as updateOrderInDB,
    updateMediaObjectRefsInMessages as updateMediaObjectRefsInDB,
    updateMessageTranscriptsInDB,
} from '@/utils/db/messages.js'
import { useConversationStore } from '@/stores/conversation.store.js'
import { usePaymentStore } from '@/stores/payment.store.js'

// Keep track of registered event names to properly clean them up for calls
const userNotificationRegisteredEvents = new Set()

/**
 * Join the user-specific channel to listen for new conversation events.
 * Also joins channels for general user notifications and calls.
 */
export async function joinUserNotificationChannel() {
    const socket = getSocket()
    if (!socket) {
        console.log('Socket not found for joinUserNotificationChannel')
        return
    }
    try {
        const idToken = await firebase.auth().currentUser.getIdToken(true)
        console.log('Joining user notification channel (myNewConversations)...')
        socket.emit('join myNewConversations', idToken)
        socket.emit('join userNotifications', idToken) // For general user_notification events
        // Get Calls related notifications (if this is the correct place/event for it)
        console.log('Joining user notification channel (myCalls)...')
        socket.emit('join myCalls', idToken)
    } catch (error) {
        console.error(
            'Error getting idToken for joinUserNotificationChannel:',
            error
        )
    }
}

export function setupUserNotificationSocketListeners() {
    const socket = getSocket()
    if (!socket) {
        console.error(
            'Cannot setup user notification socket listeners: Socket not available.'
        )
        return
    }

    // First, ensure any previous listeners are cleaned up
    cleanupUserNotificationSocketListeners()

    console.log('Setting up user notification socket listeners...')

    const onUserNotifications = (notification) => {
        const authStore = useAuthStore()
        const conversationStore = useConversationStore()
        const paymentStore = usePaymentStore()

        console.log('Socket event received: user_notifications', notification)

        switch (notification.type) {
            case 'updatedGallery':
                console.log(
                    'Updated gallery notification received',
                    notification
                )

                // Update the mediaObjectRef that matches with the sent mediaObjects in the messages
                if (notification.mediaObjects) {
                    updateMediaObjectRefsInDB(notification.mediaObjects) // Fire-and-forget for IndexedDB
                        .catch((err) =>
                            console.error(
                                'Error updating mediaObjectRefs in DB:',
                                err
                            )
                        )
                    conversationStore.updateMediaObjectRefsInMessages(
                        notification.mediaObjects
                    )
                }

                // Update transcripts if present (unlocked via purchase)
                if (notification.transcripts) {
                    updateMessageTranscriptsInDB(notification.transcripts) // Fire-and-forget
                        .catch((err) =>
                            console.error(
                                'Error updating transcripts in DB:',
                                err
                            )
                        )
                    conversationStore.updateMessageTranscripts(
                        notification.transcripts
                    )
                }
                break

            case 'user_wallet_updated':
                console.log(
                    'User wallet updated notification received',
                    notification
                )
                authStore.refreshUserBalance()
                break

            case 'updatedOrderStatus':
                if (notification.order) {
                    // Logic specific to OrderContent (unlocking content in chat)
                    if (notification.order.type === 'OrderContent') {
                        //1. Update the order in the message
                        updateOrderInDB(notification.order) // Fire-and-forget for IndexedDB
                            .catch((err) =>
                                console.error(
                                    'Error updating order in DB:',
                                    err
                                )
                            )
                        conversationStore.updateOrderInMessage(
                            notification.order
                        )

                        if (notification.order.status === 'paid') {
                            //2. React on success
                            console.log(
                                'Order paid successfully, triggering confetti.'
                            )
                            paymentStore.triggerPaymentSuccessConfetti()
                            paymentStore.setProcessingPayment(false)
                            // The PremiumContentDrawer should close automatically
                        } else if (
                            notification.order.status === 'rejected' ||
                            notification.order.status === 'cancelled'
                        ) {
                            // If order failed or was cancelled, also stop loading indicator
                            paymentStore.setProcessingPayment(false)
                        }
                    }

                    // Logic specific to OrderMessage (message pack purchase)
                    // Trigger confetti for creators when someone buys their message pack
                    if (
                        notification.order.type === 'OrderMessage' &&
                        notification.order.status === 'paid'
                    ) {
                        // Check if the current user is the creator
                        const currentUserId = authStore.userId
                        const orderCreatorId =
                            notification.order.creatorId?.toString?.() ||
                            notification.order.creatorId

                        if (
                            currentUserId &&
                            orderCreatorId &&
                            currentUserId.toString() ===
                                orderCreatorId.toString()
                        ) {
                            console.log(
                                'Message pack purchased, triggering confetti for creator.'
                            )
                            paymentStore.triggerPaymentSuccessConfetti()
                        }
                    }

                    // General logic for ALL orders (refresh balance if paid)
                    if (notification.order.status === 'paid') {
                        console.log('Order paid, refreshing user balance.')
                        authStore.refreshUserBalance()
                    }
                }

                console.log(
                    'Updated order status notification received',
                    notification
                )
                break

            case 'listing_status_updated':
                if (notification.status) {
                    const normalizedVisibility =
                        typeof notification.userSelectedListingStatus ===
                        'boolean'
                            ? notification.userSelectedListingStatus
                            : notification.userSelectedListingStatus === 'true'
                    authStore.updateListingStatus(
                        notification.status,
                        normalizedVisibility
                    )
                }
                break

            case 'KYC verification pending':
                authStore.updateKYCStatus('pending')
                break

            case 'KYC verification successful':
                authStore.updateKYCStatus('approved')
                authStore.state.showKYCSuccess = true
                break

            case 'KYC verification failed':
                authStore.updateKYCStatus('rejected')
                break

            case 'KYC verification unused':
                authStore.updateKYCStatus('unused')
                break
        }
    }

    const eventsToRegister = {
        user_notification: onUserNotifications,
    }

    // Register the event handler regardless of previous registration
    // We already cleaned up any existing handlers above
    for (const [eventName, handler] of Object.entries(eventsToRegister)) {
        if (handler && typeof handler === 'function') {
            socket.on(eventName, handler)
            userNotificationRegisteredEvents.add(eventName)
            console.log(
                `Registered user notification listener for event: ${eventName}`
            )
        }
    }

    console.log('User notification socket listeners setup complete')
}

export function cleanupUserNotificationSocketListeners() {
    const socket = getSocket()

    console.log('Cleaning up user notification socket listeners...')

    // Force clear the tracking set regardless of socket availability
    const eventsCleaned = Array.from(userNotificationRegisteredEvents)

    // Attempt to remove each event handler if socket exists
    if (socket) {
        for (const eventName of eventsCleaned) {
            try {
                socket.off(eventName)
                console.log(
                    `Removed user notification listener for ${eventName}`
                )
            } catch (error) {
                console.error(
                    `Error removing listener for ${eventName}:`,
                    error
                )
            }
            userNotificationRegisteredEvents.delete(eventName)
        }
    } else {
        console.log('Socket not available, just clearing tracking set')
    }

    // Ensure the set is completely empty
    userNotificationRegisteredEvents.clear()

    console.log('User notification socket listeners cleanup complete')
}

export default {
    setupUserNotificationSocketListeners,
    cleanupUserNotificationSocketListeners,
    joinUserNotificationChannel,
}
